import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Chip,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../hooks/useAuth';

// UserRoleを直接定義（インポートエラーを回避）
const UserRole = {
  STUDENT: 'student' as const,
  ADMIN: 'admin' as const,
} as const;

interface HeaderProps {
  title?: string;
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  title = 'ガクフドキャリア',
  onMenuClick,
  showMenuButton = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const handleAccountClick = () => {
    navigate('/mypage');
  };

  const getUserRoleDisplay = () => {
    if (!isAuthenticated || !user) return null;

    const isAdmin = user.userRole === UserRole.ADMIN;
    return (
      <Chip
        icon={isAdmin ? <AdminPanelSettingsIcon /> : <PersonIcon />}
        label={isAdmin ? '管理者' : '一般ユーザー'}
        variant="filled"
        size="small"
        sx={{
          color: 'white',
          border: 'none',
          '& .MuiChip-icon': {
            color: 'white',
          },
          backgroundColor: isAdmin 
            ? '#d32f2f' // 管理者: 赤色（視認性良好）
            : '#1976d2', // 一般ユーザー: 青色（視認性良好）
          '&:hover': {
            backgroundColor: isAdmin 
              ? '#c62828' // 管理者: より濃い赤
              : '#1565c0', // 一般ユーザー: より濃い青
          },
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 28,
        }}
      />
    );
  };

  return (
    <AppBar position="fixed" elevation={1}>
      <Toolbar>
        {showMenuButton && isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: theme.palette.primary.main,
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* ユーザー役割表示 */}
          {!isMobile && getUserRoleDisplay()}

          <Badge
            badgeContent={
              isMobile && isAuthenticated && user?.userRole === UserRole.ADMIN ? (
                <AdminPanelSettingsIcon 
                  sx={{ 
                    fontSize: 12, 
                    color: theme.palette.warning.main 
                  }} 
                />
              ) : null
            }
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'transparent',
                padding: 0,
                minWidth: 0,
                height: 16,
                borderRadius: 0,
              }
            }}
          >
            <IconButton
              color="inherit"
              aria-label="account"
              onClick={handleAccountClick}
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Badge>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;